import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { UseGetRequest } from '../../Utils/api/ApiRequests';
import { getToken, getUser } from '../../Utils/Common';
import { Button, Col,Dropdown, Form, Input, Layout, Modal, Row, Select, Space,Table, Tabs } from 'antd';
import Navbar from '../../Components/Navbar/Navbar';
import { EllipsisOutlined } from '@ant-design/icons';
import '../../CssFiles/CreditLimit.css';

export const sanitizeCreditLimit = (input) => {
    const [whole, ...fraction] = input.replace(/[^0-9.]/g, '').split('.');
    return fraction.length ? `${whole || '0'}.${fraction.join('').slice(0, 2)}` : whole;
};

export function CreditLimitInput({ value, onChange, ...props }) {
    return (
        <Input
            {...props}
            inputMode='decimal'
            value={value ?? ''}
            onChange={(event) => onChange?.(sanitizeCreditLimit(event.target.value))}
        />
    );
}


const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return Number(value).toLocaleString('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

function RefundableDeposit() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [form] = Form.useForm();
    const [selectedMember, setSelectedMember] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        async function loadMembers() {
            setIsLoading(true);

            try {
                const response = await UseGetRequest(
                    'refundable_deposit/get_members',
                    {}
                );

                if (!Array.isArray(response?.data?.members)) {
                    toast.error('Failed to load members.');
                    return;
                }

                setMembers(response.data.members);
            } catch {
                toast.error('Failed to load members.');
            } finally {
                setIsLoading(false);
            }
        }

        loadMembers();
    }, []);

    const openSetModal = () => {
        setSelectedMember(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const openEditModal = (member) => {
        setSelectedMember(member);

        form.setFieldsValue({
            member_id: member.member_id,
            member_name: member.member_name,
            customer_id: member.customer_id ?? '',
            refundable_deposit_amount: formatAmount(member.refundable_deposit_amount),
            credit_limit: member.credit_limit ?? '',
        });

        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isSaving) return;

        setIsModalOpen(false);
        setSelectedMember(null);
        form.resetFields();
    };

    const handleMemberChange = (memberId) => {
        const member = members.find(
            (item) => String(item.member_id) === String(memberId)
        );

        form.setFieldsValue({
            customer_id: member?.customer_id ?? '',
            refundable_deposit_amount: formatAmount(member?.refundable_deposit_amount),
            credit_limit: member?.credit_limit ?? '',
        });
    };

    const handleSubmit = async (values) => {
        if (isSaving) return;

        setIsSaving(true);

        try {
            const payload = new URLSearchParams({
                requester: String(getUser()),
                token: String(getToken()),
                member_id: String(values.member_id),
                credit_limit: String(values.credit_limit),
            });

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}refundable_deposit/add`,
                payload,
                {
                    headers: {
                        'api-key': process.env.REACT_APP_API_KEY,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            );

            if (
                response.data?.errors
                || !response.data?.response?.member_id
            ) {
                toast.error(
                    response.data?.message || 'Failed to save refundable deposit.'
                );
                return;
            }

            // Update the displayed limit after the backend confirms success.
            setMembers((previous) => previous.map((member) => (
                String(member.member_id) === String(values.member_id)
                    ? {
                        ...member,
                        credit_limit: values.credit_limit,
                        used_credit: member.used_credit ?? '0.00',
                        remaining_credit: Math.max(0, Number(values.credit_limit) - Number(member.used_credit ?? 0)).toFixed(2),
                        status: member.status ?? 'Active',
                    }
                    : member
            )));

            toast.success(response.data.response.message);
            setIsModalOpen(false);
            setSelectedMember(null);
            form.resetFields();
        } catch (error) {
            const message = error.response?.data?.messages?.error;

            toast.error(
                typeof message === 'string'
                    ? message
                    : 'Failed to save refundable deposit.'
            );
        } finally {
            setIsSaving(false);
        }
    };

    const columns = [
        {
            title: 'Member Name',
            dataIndex: 'member_name',
            key: 'member_name',
        },
        {
            title: 'AR Customer ID',
            dataIndex: 'customer_id',
            key: 'customer_id',
        },
        {
            title: 'Refundable Deposit Amount',
            dataIndex: 'credit_limit',
            key: 'credit_limit',
            align: 'right',
            render: (value) => formatAmount(value) || '0.00',
        },
        {
            title: 'Running Balance',
            dataIndex: 'used_credit',
            key: 'used_credit',
            align: 'right',
            render: (value) => formatAmount(value) || '0.00',
        },
        {
            title: 'Remaining Balance',
            dataIndex: 'remaining_credit',
            key: 'remaining_credit',
            align: 'right',
            render: (value) => formatAmount(value) || '0.00',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'right',
            render: (status) => status || '—',
        },
        {
            title: '',
            key: 'actions',
            width: 64,
            align: 'center',
            render: (_, member) => {

                return (
                    <Dropdown
                        trigger={['click']}
                        placement='bottomRight'
                        menu={{
                            items: [
                                {
                                    key: 'edit',
                                    label: 'Edit Limit',
                                },
                                {
                                    key: 'history',
                                    label: 'View Balance History',
                                },
                                {
                                    type: 'divider',
                                },
                                {
                                    key: 'remove',
                                    label: 'Remove Limit',
                                    danger: true,
                                },
                            ],
                            onClick: ({ key }) => {
                                switch (key) {
                                    case 'edit':
                                        openEditModal(member);
                                        break;

                                    case 'history':
                                        // Connect the balance history view next.
                                        break;

                                    case 'remove':
                                        // Connect the removal confirmation next.
                                        break;

                                    default:
                                        break;
                                }
                            },
                        }}
                    >
                        <Button
                            type='text'
                            className='credit-limit-action-button'
                            icon={<EllipsisOutlined rotate={90} />}
                            aria-label={`Credit actions for ${member.member_name}`}
                        />
                    </Dropdown>
                );
            },
        },
    ];

    const filteredMembers = members.filter((member) => {
        const term = searchTerm.trim().toLowerCase();
        const matchesStatus = activeTab === 'all'
            || String(member.status ?? '').trim().toLowerCase() === activeTab;

        return matchesStatus && `${member.member_name ?? ''} ${member.customer_id ?? ''}`
            .toLowerCase()
            .includes(term);
    });

    return (
        <Layout className='page'>
            <Navbar navKey='refundable_deposit' />
            <Layout className='content-container overflow-y'>
                <Row className='custom-padding responsive-tab-inline'>
                    <Col span={15} className='header-container'>
                        REFUNDABLE DEPOSIT
                    </Col>
                    <Col span={9} className='mt-2 responsive-tab-width' align='end'>
                        <Space className='responsive-inline'>
                            <Input.Search
                                placeholder='Search Member'
                                aria-label='Search Member'
                                className='responsive-header-input'
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                allowClear
                            />
                            <Button
                                className='button-color responsive-header-btn'
                                type='primary'
                                onClick={openSetModal}
                            >
                                + Set Limit
                            </Button>
                        </Space>
                    </Col>
                </Row>
                <Row className='page-content'>
                    <Col span={24} className='responsive-overflow-auto'>
                        <Tabs
                            type='card'
                            className='profile-tabs'
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={[
                                { key: 'all', label: 'All' },
                                { key: 'active', label: 'Active' },
                                { key: 'refunded', label: 'Refunded' },
                            ]}
                        />

                        <Table
                            key={activeTab}
                            columns={columns}
                            dataSource={filteredMembers}
                            rowKey='member_id'
                            loading={isLoading}
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 1200 }}
                        />
                    </Col>
                </Row>
                <Modal
                    className='credit-limit-modal'
                    title={
                        <span className='credit-limit-modal-title'>
                            {selectedMember
                                ? (
                                    selectedMember.credit_limit !== null
                                    && selectedMember.credit_limit !== undefined
                                    && selectedMember.credit_limit !== ''
                                        ? 'EDIT CREDIT LIMIT'
                                        : 'SET CREDIT LIMIT'
                                )
                                : 'SET CREDIT LIMIT'}
                        </span>
                    }
                    open={isModalOpen}
                    onCancel={closeModal}
                    width={600}
                    centered
                    closable={!isSaving}
                    maskClosable={!isSaving}
                    keyboard={!isSaving}
                    footer={[
                        <Button
                            key='cancel'
                            onClick={closeModal}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>,
                        <Button
                            key='save'
                            type='primary'
                            className='button-color'
                            loading={isSaving}
                            disabled={isLoading}
                            onClick={() => form.submit()}
                        >
                            Save
                        </Button>
                    ]}
                >
                    <Form
                        form={form}
                        layout='vertical'
                        onFinish={handleSubmit}
                    >
                        {selectedMember ? (
                            <>
                                <Form.Item name='member_id' hidden>
                                    <Input />
                                </Form.Item>

                                <Form.Item label='Member Name' name='member_name'>
                                    <Input disabled />
                                </Form.Item>
                            </>
                        ) : (
                            <Form.Item
                                label='Member Name'
                                name='member_id'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select a member.',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder='Select member'
                                    showSearch
                                    optionFilterProp='label'
                                    loading={isLoading}
                                    disabled={isSaving}
                                    onChange={handleMemberChange}
                                    options={members.map((member) => ({
                                        value: member.member_id,
                                        label: member.customer_id
                                            ? `${member.member_name} (${member.customer_id})`
                                            : member.member_name,
                                    }))}
                                />
                            </Form.Item>
                        )}

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label='AR Customer ID'
                                    name='customer_id'
                                >
                                    <Input placeholder='Auto-filled' disabled />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label='Running Balance'
                                    name='refundable_deposit_amount'
                                >
                                    <Input placeholder='—' disabled />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label='Refundable Deposit'
                            name='credit_limit'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a refundable deposit.',
                                },
                                {
                                    pattern: /^[0-9]{1,10}(?:\.[0-9]{1,2})?$/,
                                    message: 'Enter a valid amount with up to two decimal places.',
                                },
                            ]}
                        >
                            <CreditLimitInput
                                className='credit-limit-amount'
                                placeholder='0.00'
                                disabled={isSaving}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </Layout>
        </Layout>
    );
}

export default RefundableDeposit;
